// Pour count actions for one bottle, for the Bottle Locator panel in AdminShelfMap (Phase 3c).
//
// Self-contained on purpose: AdminShelfMap is 5,114 lines and the plan says add a component, not
// restructure the page. It owns its own fetch, its own state and its own error wording, so the
// shelf map only has to render <BottlePourCount bottleItemId={…} bottleName={…} /> and forget it.
//
// SAME CALL PATH AS THE BOTTLE CHECKS SCREEN. Both surfaces go through setPourCount and
// confirmPourEmpty in lib/api.ts, so there is one place where a count reaches Square.
//
// WHY IT FETCHES RATHER THAN TAKING PROPS
// The shelf map reads menu_items straight through PostgREST, and the browser role has no SELECT
// on `pour_count_version` — the one value every write must carry. So pour state comes from
// bar-pour-count's `list_pours`. Fetching per bottle when the panel opens also keeps the version
// as fresh as possible: a version read minutes ago is a 409 `changed` waiting to happen.
//
// EMPTY MEANS TWO DIFFERENT CALLS
// If this bottle has an open empty_check, Empty resolves it through confirm_empty — the same row
// the bottle checks screen would clear. If it has none, Empty is an ordinary count of 0, and the
// component says so, because a plain zero does not close a check (the sync only clears one when
// Square goes ABOVE zero) and staff should not think it did.

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import {
  ApiError,
  confirmPourEmpty,
  listPoursForBottles,
  setPourCount,
  type BottlePour,
  type PourFillLevel,
} from "../../lib/api";

interface Props {
  /** The bottle's menu_items id, as the shelf map knows it. */
  bottleItemId: string;
  /** Full product name, for the confirmation prompt. Never an abbreviation. */
  bottleName: string;
}

type ArmedAction =
  | { kind: "empty" }
  | { kind: "fill"; level: PourFillLevel; label: string }
  | { kind: "exact"; pours: number };

/** Mirrors MAX_MANUAL_POURS in supabase/functions/_shared/pours.ts, which refuses more. Checked
 *  here too so a typo comes back instantly instead of after a round trip to Square's doorstep. */
const MAX_MANUAL_POURS = 200;

const FILL_BUTTONS: { level: PourFillLevel; label: string }[] = [
  { level: "full", label: "Full" },
  { level: "three_quarters", label: "3/4" },
  { level: "half", label: "1/2" },
  { level: "quarter", label: "1/4" },
];

function confirmPrompt(action: ArmedAction, bottle: string, check: "open" | "none" | "unknown"): string {
  if (action.kind === "empty") {
    if (check === "open") return `Mark ${bottle} empty? Square goes to 0 and this bottle's open check is cleared.`;
    if (check === "none") {
      return `Mark ${bottle} empty? Square goes to 0. There is no open check on this bottle, so nothing else changes.`;
    }
    // The check lookup failed. Saying "there is no open check" here would be a guess, and a wrong
    // one leaves a real check open with staff believing they cleared it.
    return `Mark ${bottle} empty? Square goes to 0. Whether this bottle has an open check could not be read — if it does, clear it from Bottle Checks.`;
  }
  if (action.kind === "fill") {
    return `Set ${bottle} to ${action.label}? The pours are worked out from the bottle size and sent to Square.`;
  }
  return `Set ${bottle} to ${action.pours} pour${action.pours === 1 ? "" : "s"}? That count is sent to Square.`;
}

/** Whether this pour has an open empty check, INCLUDING the case where that could not be read.
 *  Collapsing "unknown" into "none" is how a screen ends up telling staff it cleared something it
 *  did not, so the three states stay three states all the way to the wording. */
function checkState(pour: BottlePour): "open" | "none" | "unknown" {
  if (!pour.open_check_known) return "unknown";
  return pour.open_empty_check_id ? "open" : "none";
}

/** The endpoint's messages are written for staff, so they are passed through; these add what the
 *  component knows that the message does not. */
function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.changed) return `${e.message}`;
    if (e.busy) return "Another tablet is saving this bottle right now. Nothing was changed — try again in a moment.";
    if (e.saved) return `${e.message} The app catches up within 15 minutes.`;
    if (e.status === 401) return "This account cannot set counts. Ask a lead or manager.";
    if (e.status === 408) {
      return "That took too long to answer. Square may or may not have it — reopen this bottle and check the count before trying again.";
    }
    return e.message;
  }
  return "Couldn't reach the bar system. Check the tablet's connection and try again.";
}

export default function BottlePourCount({ bottleItemId, bottleName }: Props) {
  const [pours, setPours] = useState<BottlePour[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [armed, setArmed] = useState<{ pourItemId: string; action: ArmedAction } | null>(null);
  const [exact, setExact] = useState<Record<string, string>>({});
  /** Keyed by pour, like the guard: a bottle may carry two pours, and one finishing must not
   *  wipe what the other just reported. */
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Pours whose row is showing a pre-save count because the refresh after the save failed. */
  const [stale, setStale] = useState<string[]>([]);
  /** Single-flight keyed on the POUR, matching what the endpoint claims. The ref is the guard
   *  (mutated before any await); the state is what disables the buttons. A bottle may carry more
   *  than one pour, and those are separate resources — blocking both would be a panel-wide lock. */
  const inFlight = useRef<Set<string>>(new Set());
  const [busyPours, setBusyPours] = useState<string[]>([]);

  /** Returns whether the refresh actually landed, so a caller can flag what is now stale. */
  const load = useCallback(async (initial: boolean): Promise<boolean> => {
    if (initial) setLoadFailed(false);
    try {
      setPours(await listPoursForBottles([bottleItemId]));
      setLoadFailed(false);
      setStale([]);
      return true;
    } catch {
      // Only the FIRST load may replace the panel with an error. A refresh after a save must not:
      // the save's outcome is the thing staff need to read, and swapping it for "couldn't load"
      // hides the fact that Square already has the count.
      if (initial) {
        setPours([]);
        setLoadFailed(true);
      }
      return false;
    }
  }, [bottleItemId]);

  useEffect(() => {
    // A different bottle means different pour state; drop everything the last one said.
    setArmed(null);
    setNotes({});
    setErrors({});
    setStale([]);
    setExact({});
    setPours(null);
    load(true);
  }, [bottleItemId, load]);

  const say = useCallback((pourId: string, text: string) => {
    setNotes((n) => ({ ...n, [pourId]: text }));
  }, []);

  const run = useCallback(
    async (pour: BottlePour, action: ArmedAction) => {
      const pourId = pour.pour_item_id;
      if (inFlight.current.has(pourId)) return;
      inFlight.current.add(pourId);
      setBusyPours((p) => (p.includes(pourId) ? p : [...p, pourId]));
      setArmed(null);
      setNotes((n) => ({ ...n, [pourId]: "" }));
      setErrors((e) => ({ ...e, [pourId]: "" }));
      setStale((p) => p.filter((x) => x !== pourId));
      try {
        if (action.kind === "empty" && pour.open_empty_check_id) {
          const res = await confirmPourEmpty(pour.open_empty_check_id, {
            pour_count_version: pour.pour_count_version,
          });
          say(pourId,
            res.already_resolved
              ? `Already cleared${res.resolved_by ? ` by ${res.resolved_by}` : ""} — nothing was sent to Square.`
              : "Marked empty. Square is at 0 and this bottle's check is cleared."
          );
        } else if (action.kind === "empty") {
          const res = await setPourCount(pour.pour_item_id, { fill: "empty" }, {
            pour_count_version: pour.pour_count_version,
          });
          say(pourId,
            checkState(pour) === "none"
              ? `Square is at ${res.pours_set}. No check was open on this bottle, so nothing else changed.`
              : `Square is at ${res.pours_set}. This bottle's checks could not be read, so if one is open it is still open — look in Bottle Checks.`
          );
        } else {
          const amount = action.kind === "fill" ? { fill: action.level } : { pours: action.pours };
          const res = await setPourCount(pour.pour_item_id, amount, {
            pour_count_version: pour.pour_count_version,
          });
          const size = action.kind === "fill" && res.bottle_size_label ? ` (${res.bottle_size_label})` : "";
          say(pourId,
            `Count set to ${res.pours_set}${size}. Square has it now.` +
              (checkState(pour) === "open"
                ? " This bottle's check clears at the next sync, within 15 minutes."
                : checkState(pour) === "unknown"
                ? " This bottle's checks could not be read — if one is open, it clears at the next sync."
                : "") +
              (res.notice ? ` ${res.notice}` : "")
          );
        }
        // N2: if this refresh fails the row above the note still shows the pre-save count, so
        // say so rather than leaving two contradictory numbers on screen.
        if (!(await load(false))) setStale((p) => (p.includes(pourId) ? p : [...p, pourId]));
      } catch (e) {
        setErrors((prev) => ({ ...prev, [pourId]: describeError(e) }));
        // Whatever happened, the version this component holds is now suspect.
        await load(false);
      } finally {
        inFlight.current.delete(pourId);
        setBusyPours((p) => p.filter((x) => x !== pourId));
      }
    },
    [load]
  );

  if (pours === null) {
    return <div style={styles.wrap}><div style={styles.muted}>Loading pour count…</div></div>;
  }
  if (loadFailed) {
    return (
      <div style={styles.wrap}>
        <div style={styles.muted}>
          Couldn't load this bottle's pour count.{" "}
          <button type="button" style={styles.linkBtn} onClick={() => load(true)}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  // Most shelved bottles are not poured by the ounce; say nothing rather than show dead controls.
  if (pours.length === 0) return null;

  return (
    <div style={styles.wrap}>
      <div style={styles.heading}>Pour count</div>
      {pours.map((pour) => {
        const busy = busyPours.includes(pour.pour_item_id);
        const isArmed = armed?.pourItemId === pour.pour_item_id;
        const typed = exact[pour.pour_item_id] ?? "";

        return (
          <div key={pour.pour_item_id} style={styles.pourBlock}>
            <div style={styles.stateLine}>
              {pours.length > 1 && <strong style={{ color: "var(--text)" }}>{pour.pour_name} · </strong>}
              {pour.pours_remaining === null ? "no count" : `${pour.pours_remaining} pours`}
              {pour.pour_status ? ` · ${pour.pour_status.replace(/_/g, " ")}` : ""}
              {checkState(pour) === "open" && <span style={styles.checkChip}>Empty check open</span>}
              {checkState(pour) === "unknown" && <span style={styles.unknownChip}>Checks unavailable</span>}
            </div>

            {stale.includes(pour.pour_item_id) && (
              <div style={styles.muted}>The count above is from before the last save — reopen this bottle to refresh it.</div>
            )}
            {notes[pour.pour_item_id] && (
              <div style={styles.note} role="status">
                {notes[pour.pour_item_id]}
              </div>
            )}
            {errors[pour.pour_item_id] && (
              <div style={styles.error} role="alert">
                {errors[pour.pour_item_id]}
              </div>
            )}

            {!pour.actionable ? (
              <div style={styles.muted}>
                This pour has no Square variation, so its count can only be set in Square.
              </div>
            ) : isArmed ? (
              <div style={styles.confirmBar} role="status">
                <span style={styles.confirmText}>
                  {confirmPrompt(armed.action, bottleName, checkState(pour))}
                </span>
                <span style={styles.btnRow}>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnConfirm, ...(busy ? styles.btnBusy : null) }}
                    onClick={() => run(pour, armed.action)}
                    disabled={busy}
                  >
                    Confirm
                  </button>
                  <button type="button" style={styles.btn} onClick={() => setArmed(null)}>
                    Cancel
                  </button>
                </span>
              </div>
            ) : (
              <div style={styles.btnRow}>
                <button
                  type="button"
                  style={{ ...styles.btn, ...styles.btnEmpty, ...(busy ? styles.btnBusy : null) }}
                  onClick={() => setArmed({ pourItemId: pour.pour_item_id, action: { kind: "empty" } })}
                  disabled={busy}
                >
                  Empty
                </button>
                {FILL_BUTTONS.map((f) => (
                  <button
                    key={f.level}
                    type="button"
                    style={{ ...styles.btn, ...(busy ? styles.btnBusy : null) }}
                    onClick={() =>
                      setArmed({
                        pourItemId: pour.pour_item_id,
                        action: { kind: "fill", level: f.level, label: f.label },
                      })
                    }
                    disabled={busy}
                  >
                    {f.label}
                  </button>
                ))}
                <form
                  style={styles.exactForm}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const n = Number(typed);
                    const complain = (m: string) =>
                      setErrors((prev) => ({ ...prev, [pour.pour_item_id]: m }));
                    if (!/^\d+$/.test(typed.trim()) || !Number.isInteger(n)) {
                      complain("Enter a whole number of pours.");
                      return;
                    }
                    if (n === 0) {
                      complain("Use Empty for a finished bottle.");
                      return;
                    }
                    if (n > MAX_MANUAL_POURS) {
                      complain(`That is more than ${MAX_MANUAL_POURS} pours — check the number.`);
                      return;
                    }
                    complain("");
                    setArmed({ pourItemId: pour.pour_item_id, action: { kind: "exact", pours: n } });
                  }}
                >
                  <input
                    type="number"
                    min={0}
                    step={1}
                    inputMode="numeric"
                    value={typed}
                    onChange={(e) => setExact((p) => ({ ...p, [pour.pour_item_id]: e.target.value }))}
                    placeholder="pours"
                    style={styles.input}
                    aria-label={`Exact pours left for ${bottleName}`}
                    disabled={busy}
                  />
                  <button type="submit" style={{ ...styles.btn, ...(busy ? styles.btnBusy : null) }} disabled={busy}>
                    Set
                  </button>
                </form>
              </div>
            )}
          </div>
        );
      })}

      {busyPours.length > 0 && <div style={styles.muted}>Sending to Square…</div>}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A2723)",
    borderRadius: 10,
    padding: 12,
    marginBottom: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  heading: { fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--muted, #A39D98)" },
  pourBlock: { display: "flex", flexDirection: "column", gap: 8 },
  stateLine: { fontSize: 12, color: "var(--muted, #A39D98)", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  unknownChip: {
    fontSize: 10,
    fontWeight: 800,
    padding: "2px 7px",
    borderRadius: 4,
    background: "rgba(163,157,152,0.15)",
    color: "var(--muted, #A39D98)",
  },
  checkChip: {
    fontSize: 10,
    fontWeight: 800,
    padding: "2px 7px",
    borderRadius: 4,
    background: "rgba(224,80,64,0.15)",
    color: "#E05040",
  },
  btnRow: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
  btn: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border, #2A2723)",
    color: "var(--text, #F7F5F0)",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 44,
    minWidth: 56,
  },
  btnEmpty: { background: "var(--gold, #F9AF36)", color: "#0D0B08", borderColor: "var(--gold, #F9AF36)" },
  btnConfirm: { background: "var(--gold, #F9AF36)", color: "#0D0B08", borderColor: "var(--gold, #F9AF36)", fontWeight: 800 },
  btnBusy: { opacity: 0.5, cursor: "progress" },
  exactForm: { display: "inline-flex", gap: 6, alignItems: "center" },
  input: {
    width: 84,
    minHeight: 44,
    background: "var(--surface, #16130F)",
    border: "1px solid var(--border, #2A2723)",
    borderRadius: 8,
    color: "var(--text, #F7F5F0)",
    padding: "8px 10px",
    fontSize: 13,
  },
  confirmBar: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    background: "rgba(249,175,54,0.08)",
    border: "1px solid rgba(249,175,54,0.45)",
    borderRadius: 8,
    padding: 10,
  },
  confirmText: { fontSize: 12, lineHeight: 1.5, flex: "1 1 220px", color: "var(--text, #F7F5F0)" },
  muted: { fontSize: 12, color: "var(--muted, #A39D98)", lineHeight: 1.5 },
  note: { fontSize: 12, lineHeight: 1.5, color: "#86efac" },
  error: { fontSize: 12, lineHeight: 1.5, color: "#E8897F" },
  linkBtn: {
    background: "none",
    border: "none",
    color: "var(--gold, #F9AF36)",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: 12,
    padding: 0,
  },
};
